import { useState, useEffect, RefObject } from 'react';
// thanks to https://usehooks.com/useOnScreen/

type UseIsOnScreenProps<T extends Element> = {
  ref: RefObject<T>;
  threshold?: number;
  onView: () => void;
  rootMargin?: string;
};

export function useIsOnScreen<T extends Element>({
  ref,
  threshold = 0,
  onView = () => {},
  rootMargin = '0px',
}: UseIsOnScreenProps<T>): boolean {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const supportsIntersectionObserver =
      __CLIENT__ && 'IntersectionObserver' in window;

    // If we're SSRing don't go though
    if (__SERVER__) return;

    // if the browser doesn't support IO execute the function and return
    if (!supportsIntersectionObserver) {
      onView();
      return;
    }
    if (!ref.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onView();
        }
        setIntersecting(entry.isIntersecting);
      },
      {
        threshold,
        rootMargin,
      },
    );
    observer.observe(ref.current);
    return () => {
      if (ref.current !== null) {
        observer.unobserve(ref.current);
      }
    };
  }, []);
  return isIntersecting;
}
